import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Translations come straight from the mobile app's locale files (single source of
// truth) via the `@locales` alias → repo-root /locales. All 5 are COMPLETE
// (1,911 keys each) per CLAUDE.md.
import en from '@locales/en.json'
import es from '@locales/es.json'
import fr from '@locales/fr.json'
import de from '@locales/de.json'
import pt from '@locales/pt.json'

export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
}

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  de: { translation: de },
  pt: { translation: pt },
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    // Some web keys may collide with the app's nested namespaces (a key that
    // resolves to an OBJECT in the shared locale files). Fall back to the
    // English default passed as the 2nd arg instead of rendering the
    // "returned an object" error.
    returnedObjectHandler: (key: string, _value: unknown, options?: { defaultValue?: unknown }) => {
      const dv = options?.defaultValue
      return typeof dv === 'string' ? dv : key.split('.').pop() || key
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'app_language',
      caches: ['localStorage'],
    },
  })
  .then(() => {
    document.documentElement.lang = i18n.language
  })

i18n.on('languageChanged', (lang) => {
  document.documentElement.lang = lang
})

export async function changeLanguage(lang: Language): Promise<void> {
  await i18n.changeLanguage(lang)
}

/**
 * The AI callables expect the language NAME (e.g. 'Español'), not the code —
 * mobile sends LANGUAGE_NAMES[getCurrentLanguage()].
 */
export function currentLanguageName(): string {
  const lang = i18n.resolvedLanguage || i18n.language
  return LANGUAGE_NAMES[lang as Language] || LANGUAGE_NAMES.en
}

export default i18n
